import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button, Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Header from "../components/header";
import Footer from "../components/footer";
import getApiBase from "../components/getApiBase";

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [userRole, setUserRole] = useState(null);
  const hasVerified = useRef(false); // Prevent double verification
  // Bug #2 fix: Ref so button/redirect always have correct role. Conditional break: redirect/button
  // handlers ran before setUserRole had flushed, so userRole was still null and login defaulted to Car Owner.
  // We set verifiedRoleRef.current = data.role immediately when the API returns and read the ref first
  // when building the login URL; if no role (e.g. failed verification), we use /login?fromVerify=1 so
  // Login shows the role picker.
  const verifiedRoleRef = useRef(null);

  useEffect(() => {
    // Prevent double verification (React Strict Mode in dev causes double renders)
    if (hasVerified.current) {
      console.log("⚠️ Verification already attempted, skipping duplicate call");
      return;
    }
    
    if (token && !hasVerified.current) {
      hasVerified.current = true;
      verifyToken();
    }
  }, [token]);

  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      // Bug #2: use ref first so we have role even if state hasn't updated yet (conditional break).
      const roleFromRef = verifiedRoleRef.current;
      const role = roleFromRef ?? userRole;
      const loginRole = role === "Owner" ? "Mechanic" : (role || "User");
      const loginUrl = role ? `/login?role=${loginRole}` : "/login?fromVerify=1";
      navigate(loginUrl);
    }
  }, [success, countdown, navigate, userRole]);

  const verifyToken = async () => {
    try {
      console.log("🔍 Starting email verification for token:", token?.substring(0, 8) + "...");
      const response = await fetch(`${getApiBase()}/api/auth/verify-email/${token}`, {
        method: "GET",
        credentials: "include",
      });

      const data = await response.json();
      console.log("📥 Verification response:", { ok: response.ok, success: data.success, message: data.message });

      if (response.ok && data.success) {
        setSuccess(true);
        setMessage(data.message || "Email verified successfully!");
        // Bug #2: Store role in ref immediately so redirect/button handlers see it (state is async).
        if (data.role) {
          setUserRole(data.role);
          verifiedRoleRef.current = data.role;
        }
      } else {
        setSuccess(false);
        setMessage(data.message || "Verification failed. The link may be invalid or expired.");
        // Bug #2: set ref so "Try to Login" can use role if API returned it despite failure.
        if (data.role) {
          setUserRole(data.role);
          verifiedRoleRef.current = data.role;
        } else {
          verifiedRoleRef.current = null;
        }
      }
    } catch (error) {
      console.error("Verification error:", error);
      setSuccess(false);
      setMessage("Failed to verify email. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f7f9fc",
          px: 2,
          py: 6,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 500,
            width: "100%",
            p: 4,
            textAlign: "center",
            borderRadius: 3,
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={60} sx={{ color: "#e63926", mb: 3 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Verifying Your Email...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we confirm your email address.
              </Typography>
            </>
          ) : success ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: "#059669", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#059669", mb: 2 }}>
                Email Verified! ✅
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can now log in to your account and access all features.
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, fontWeight: 600, color: "#e63926" }}>
                Redirecting to login in {countdown} seconds...
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  // Bug #2: read ref first (conditional break was state not updated before click).
                  const roleFromRef = verifiedRoleRef.current;
                  const loginRole = roleFromRef === "Owner" ? "Mechanic" : (roleFromRef === "Mechanic" ? "Mechanic" : (userRole === "Owner" ? "Mechanic" : (userRole || "User")));
                  const loginUrl = `/login?role=${loginRole}`;
                  navigate(loginUrl);
                }}
                sx={{
                  backgroundColor: "#e63926",
                  "&:hover": { backgroundColor: "#d32f2f" },
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: "#e63926", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#e63926", mb: 2 }}>
                Verification Failed
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This could happen if:
              </Typography>
              <Box sx={{ textAlign: "left", mb: 3, px: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • The verification link has expired (7 days)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • The link was already used
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • The link is invalid or corrupted
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/signup")}
                  sx={{
                    borderColor: "#e63926",
                    color: "#e63926",
                    "&:hover": { borderColor: "#d32f2f", backgroundColor: "rgba(230, 57, 38, 0.04)" },
                  }}
                >
                  Sign Up Again
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    // Bug #2: ref first so we get Mechanic when API returned role; else fromVerify=1 for role picker.
                    const roleFromRef = verifiedRoleRef.current;
                    const role = roleFromRef ?? userRole;
                    const loginRole = role === "Owner" ? "Mechanic" : (role || "User");
                    const loginUrl = role ? `/login?role=${loginRole}` : "/login?fromVerify=1";
                    navigate(loginUrl);
                  }}
                  sx={{
                    backgroundColor: "#e63926",
                    "&:hover": { backgroundColor: "#d32f2f" },
                  }}
                >
                  Try to Login
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
      <Footer />
    </>
  );
};

export default VerifyEmail;

